import React, { useCallback, useEffect } from "react";
import queryString from "query-string";

import Inline from "../../../components/lib/Inline";
import Box from "../../../components/lib/Box";
import Stack from "../../../components/lib/Stack";
import Icon from "../../../components/lib/Icon";
import Text from "../../../components/lib/Text";
import Button from "../../../components/lib/Button";
import Alert from "../../../components/lib/Alert";
import DownloadPOSTButtonExt from "../../private_index/components/DownloadPOSTButtonExt";
// @ts-expect-error
import { logAsyncOperationError } from "../../../utils/logging";
import { RadioGroup, RadioGroupItem } from "../../../components/lib/RadioGroup";
import { ButtonGroupRight } from "../../../components/lib/ButtonGroup";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
  DialogDescription,
  DialogActions,
} from "../../../components/lib/Dialog";

import type {
  FiltersQueryObject,
  UrlQueryObject,
} from "../../../components/tables/types";
import type { Values } from "../types";
import { FetchAPIResponse } from "../../../types/fetch";
import { useVal5KAdminContext } from "../context/Val5KAdminContext";

const QUESTIONS_ORDERING_TYPES = {
  BY_COUNTRY:
    "attempt__review__countries_string, attempt__review__jobs__title, question__statement",
  BY_TITLE:
    "attempt__review__jobs__title, question__statement, attempt__review__countries_string",
  BY_QUESTION:
    "question__statement, attempt__review__jobs__title, attempt__review__countries_string",
} as const;

export type QUESTIONS_ORDERING_TYPES_TYPE = Values<typeof QUESTIONS_ORDERING_TYPES>;

type QuestionsExportOrderingBlockProps = {
  ordering: any;
  onChange: (value: any) => void;
};

const QuestionsExportOrderingBlock = (props: QuestionsExportOrderingBlockProps) => {
  const { ordering, onChange } = props;

  return (
    <Stack css={{ alignItems: "flex-start", gap: "$2" }}>
      <Text bold>Select the way of ordering exported data:</Text>
      <RadioGroup defaultValue={ordering} onValueChange={onChange}>
        <Inline css={{ gap: "$2" }}>
          <RadioGroupItem value={QUESTIONS_ORDERING_TYPES.BY_COUNTRY}>
            By Country
          </RadioGroupItem>
          <RadioGroupItem value={QUESTIONS_ORDERING_TYPES.BY_TITLE}>
            By Job Title
          </RadioGroupItem>
          <RadioGroupItem value={QUESTIONS_ORDERING_TYPES.BY_QUESTION}>
            By Question Title
          </RadioGroupItem>
        </Inline>
      </RadioGroup>
    </Stack>
  );
};
QuestionsExportOrderingBlock.displayName = "QuestionsExportOrderingBlock";

type GetQueryArgsFuncType = (
  ordering: QUESTIONS_ORDERING_TYPES_TYPE,
  isCurrentPageOnly: boolean
) => UrlQueryObject | null;

type QuestionsExportConfigProps = {
  show: boolean;
  itemsTotal: number;
  itemsPerPage: number;
  getQueryArgs: GetQueryArgsFuncType;
  getFormData: () => FiltersQueryObject;
  onClose: () => void;
  onError: (err: Error) => void;
};

const QuestionsExportConfig = (props: QuestionsExportConfigProps) => {
  const { show, itemsTotal, itemsPerPage, getQueryArgs, getFormData, onClose, onError } =
    props;
  const hasData = itemsTotal > 0;
  const isSinglePageExport = hasData && itemsTotal <= itemsPerPage;

  const { showModalError, fetchArgusAPI } = useVal5KAdminContext();

  // state

  const [ordering, setOrdering] = React.useState(QUESTIONS_ORDERING_TYPES.BY_COUNTRY);

  // effects

  useEffect(() => {
    if (!show) {
      setOrdering(QUESTIONS_ORDERING_TYPES.BY_COUNTRY);
    }
  }, [show]);

  // handlers

  const handleError = useCallback(
    (err: Error) => {
      onClose();
      onError(err);
    },
    [onError, onClose]
  );

  const handleExportValidate = useCallback(
    async (queryArgsData: UrlQueryObject, formData: FiltersQueryObject) => {
      try {
        const params = Object.assign({}, queryArgsData, formData);
        const response: FetchAPIResponse<{ count: number }> = await fetchArgusAPI(
          "attempts/questions-feedback/count/",
          {
            method: "get",
            params: queryString.stringify(params),
          }
        );

        const { count: questionsFeedbackCount } = response.data;

        if (!questionsFeedbackCount) {
          onClose();
          showModalError("No questions feedback found for selected rows.");
          return false;
        }

        return true;
      } catch (err: any) {
        logAsyncOperationError("checkQuestionsFeedbackExport", err);
        showModalError("Error occurred during questions feedback checks.");
        throw err;
      }
    },
    [onClose, showModalError, fetchArgusAPI]
  );

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent css={{ minWidth: "700px" }}>
        <DialogTitle asChild>
          <Inline css={{ justifyContent: "space-between" }}>
            <Box>
              <Text as="h4">Export questions and answers to Excel.</Text>
            </Box>
            <DialogClose asChild>
              <Icon icon="times" />
            </DialogClose>
          </Inline>
        </DialogTitle>
        <DialogDescription css={{ backgroundColor: "inherit" }} asChild>
          <Stack css={{ alignItems: "flex-start", gap: "$4" }}>
            {!hasData && <Text bold>No data to export.</Text>}
            {hasData && (
              <Alert fill color="warning">
                <Text>
                  <Text bold underline italic>
                    Please Note
                  </Text>
                  : Exporting questions list could take awhile depending on the size of
                  the output.
                </Text>
              </Alert>
            )}
            {hasData && (
              <QuestionsExportOrderingBlock ordering={ordering} onChange={setOrdering} />
            )}
            {hasData && (
              <Stack css={{ alignItems: "flex-start", gap: "$2" }}>
                <Text bold>Export data using following options:</Text>
                {!isSinglePageExport && (
                  <DownloadPOSTButtonExt
                    size="small"
                    icon={["far", "file-excel"]}
                    color="brand"
                    variant="outlined"
                    loadingText="Export questions for current page only."
                    service="argus"
                    endpoint="v1/attempts/questions-feedback/export/"
                    getQueryArgs={() => getQueryArgs(ordering, true)}
                    getFormData={getFormData}
                    onValidate={handleExportValidate}
                    onSuccess={onClose}
                    onError={handleError}
                  >
                    Export questions for current page only.
                  </DownloadPOSTButtonExt>
                )}
                <DownloadPOSTButtonExt
                  size="small"
                  icon={["far", "file-excel"]}
                  color="brand"
                  variant="outlined"
                  loadingText="Export questions for the whole list of surveys."
                  service="argus"
                  endpoint="v1/attempts/questions-feedback/export/"
                  getQueryArgs={() => getQueryArgs(ordering, false)}
                  getFormData={getFormData}
                  onValidate={handleExportValidate}
                  onSuccess={onClose}
                  onError={handleError}
                >
                  Export questions for the whole list of surveys.
                </DownloadPOSTButtonExt>
              </Stack>
            )}
          </Stack>
        </DialogDescription>
        <DialogActions>
          <ButtonGroupRight fill>
            <Button size="large" onClick={onClose}>
              Close
            </Button>
          </ButtonGroupRight>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
QuestionsExportConfig.displayName = "QuestionsExportConfig";

export default QuestionsExportConfig;
